import { WordList, calculateNetDistance } from '../Wordfinder/wordfinderUtils';

export interface PathResult {
  path: string[];
  length: number;
}

export interface SearchProgress {
  searchesPerformed: number;
  pathsFound: number;
}

export interface PathSearchResult {
  paths: PathResult[];
  hasMore: boolean;
}

/**
 * Find neighbors of a word (words within maxMorph distance)
 * This is computed on-demand, not precomputed
 * Excludes words in the excludedWords set and words shorter than minWordLength
 */
const findNeighbors = (
  word: string,
  wordList: WordList,
  maxMorph: number,
  excludedWords: Set<string>,
  minWordLength: number
): string[] => {
  const neighbors: string[] = [];
  for (const candidate of wordList) {
    if (candidate === word || excludedWords.has(candidate) || candidate.length < minWordLength) continue;
    const dist = calculateNetDistance(word, candidate);
    if (dist > 0 && dist <= maxMorph) {
      neighbors.push(candidate);
    }
  }
  return neighbors;
};

/**
 * Find morph paths using bidirectional BFS
 * Searches from both start and end, computing neighbors on-demand
 * Reports progress as it searches
 * Excludes words in the excludedWords set and words shorter than minWordLength
 */
export const findMorphPaths = (
  startWord: string,
  endWord: string,
  wordList: WordList,
  maxPathLength: number,
  maxMorph: number,
  maxResults: number = 50,
  onPathFound?: (path: PathResult) => void,
  onProgress?: (progress: SearchProgress) => void,
  excludedWords: Set<string> = new Set(),
  minWordLength: number = 1
): PathSearchResult => {
  if (startWord === endWord) {
    return { paths: [], hasMore: false };
  }

  let searchesPerformed = 0;
  const results: PathResult[] = [];
  let stoppedEarly = false;

  // Start: Find neighbors of start word
  const startNeighbors = findNeighbors(startWord, wordList, maxMorph, excludedWords, minWordLength);
  searchesPerformed++;
  if (onProgress) onProgress({ searchesPerformed, pathsFound: results.length });

  // End: Find neighbors of end word
  const endNeighbors = findNeighbors(endWord, wordList, maxMorph, excludedWords, minWordLength);
  searchesPerformed++;
  if (onProgress) onProgress({ searchesPerformed, pathsFound: results.length });

  // Check for direct connection (2-step path)
  if (startNeighbors.includes(endWord)) {
    results.push({ path: [startWord, endWord], length: 2 });
    if (onPathFound) onPathFound(results[results.length - 1]);
    if (results.length >= maxResults) {
      stoppedEarly = true;
      return { paths: results, hasMore: true };
    }
  }

  // Check for 3-step paths (overlap in neighbors)
  for (const neighbor of startNeighbors) {
    if (endNeighbors.includes(neighbor)) {
      results.push({ path: [startWord, neighbor, endWord], length: 3 });
      if (onPathFound) onPathFound(results[results.length - 1]);
      if (results.length >= maxResults) {
        stoppedEarly = true;
        return { paths: results, hasMore: true };
      }
    }
  }

  // Bidirectional BFS: track paths from both ends
  const fromStart = new Map<string, string[]>(); // word -> path from start
  const fromEnd = new Map<string, string[]>();   // word -> path to end

  for (const neighbor of startNeighbors) {
    fromStart.set(neighbor, [startWord, neighbor]);
  }

  for (const neighbor of endNeighbors) {
    fromEnd.set(neighbor, [neighbor, endWord]);
  }

  let currentLength = 2;

  while (currentLength < maxPathLength && results.length < maxResults) {
    if (fromStart.size === 0 && fromEnd.size === 0) break;

    // Expand the smaller frontier
    const expandStart = fromStart.size <= fromEnd.size && fromStart.size > 0;

    if (expandStart) {
      const newFrontier = new Map<string, string[]>();

      for (const [word, pathFromStart] of fromStart) {
        const neighbors = findNeighbors(word, wordList, maxMorph, excludedWords, minWordLength);
        searchesPerformed++;
        if (onProgress && searchesPerformed % 5 === 0) {
          onProgress({ searchesPerformed, pathsFound: results.length });
        }

        for (const neighbor of neighbors) {
          // Avoid cycles in this path
          if (pathFromStart.includes(neighbor)) continue;

          const newPath = [...pathFromStart, neighbor];

          // Check if we reached the end
          if (neighbor === endWord) {
            results.push({ path: newPath, length: newPath.length });
            if (onPathFound) onPathFound(results[results.length - 1]);
            if (results.length >= maxResults) {
              stoppedEarly = true;
              return { paths: results, hasMore: true };
            }
          }

          // Check if we connected to the end frontier
          if (fromEnd.has(neighbor)) {
            const pathToEnd = fromEnd.get(neighbor)!;
            const fullPath = [...newPath, ...pathToEnd.slice(1)];
            results.push({ path: fullPath, length: fullPath.length });
            if (onPathFound) onPathFound(results[results.length - 1]);
            if (results.length >= maxResults) {
              stoppedEarly = true;
              return { paths: results, hasMore: true };
            }
          }

          // Add to new frontier if not already explored from this end
          if (!fromStart.has(neighbor) && !newFrontier.has(neighbor)) {
            newFrontier.set(neighbor, newPath);
          }
        }
      }

      fromStart.clear();
      for (const [word, path] of newFrontier) {
        fromStart.set(word, path);
      }
    } else if (fromEnd.size > 0) {
      const newFrontier = new Map<string, string[]>();

      for (const [word, pathToEnd] of fromEnd) {
        const neighbors = findNeighbors(word, wordList, maxMorph, excludedWords, minWordLength);
        searchesPerformed++;
        if (onProgress && searchesPerformed % 5 === 0) {
          onProgress({ searchesPerformed, pathsFound: results.length });
        }

        for (const neighbor of neighbors) {
          // Avoid cycles
          if (pathToEnd.includes(neighbor)) continue;

          const newPath = [neighbor, ...pathToEnd];

          // Check if we reached the start
          if (neighbor === startWord) {
            results.push({ path: newPath, length: newPath.length });
            if (onPathFound) onPathFound(results[results.length - 1]);
            if (results.length >= maxResults) {
              stoppedEarly = true;
              return { paths: results, hasMore: true };
            }
          }

          // Check if we connected to the start frontier
          if (fromStart.has(neighbor)) {
            const pathFromStart = fromStart.get(neighbor)!;
            const fullPath = [...pathFromStart, ...newPath.slice(1)];
            results.push({ path: fullPath, length: fullPath.length });
            if (onPathFound) onPathFound(results[results.length - 1]);
            if (results.length >= maxResults) {
              stoppedEarly = true;
              return { paths: results, hasMore: true };
            }
          }

          // Add to new frontier
          if (!fromEnd.has(neighbor) && !newFrontier.has(neighbor)) {
            newFrontier.set(neighbor, newPath);
          }
        }
      }

      fromEnd.clear();
      for (const [word, path] of newFrontier) {
        fromEnd.set(word, path);
      }
    }

    currentLength++;
  }

  return { paths: results, hasMore: false };
};
